/* eslint-disable react-hooks/exhaustive-deps */
import { useOktaAuth } from '@okta/okta-react';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import {
    ExpenseUserGroupEnum,
    ExpenseUserGroups,
    TimesheetUserGroupEnum,
    TimesheetUserGroups,
    ExpenseUserGroupProdEnum,
    ExpenseUserProdGroups,
    TimesheetUserGroupProdEnum,
    TimesheetUserProdGroups,
    InvoicingUserGroupEnum,
    InvoicingUserGroupProdEnum,
    InvoicingUserGroups,
    InvoicingUserProdGroups,
} from '../Configurations/OktaUserGroup.enum';

export const AdminAuthContext = createContext();

export function AdminAuthProvider(props) {
    const { children } = props;
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [adminUser, setAdminUser] = useState('');
    const { oktaAuth, authState } = useOktaAuth();
    const [isTimesheetUser, setIsTimesheetUser] = useState(false);
    const [isExpenseUser, setIsExpenseUser] = useState(false);
    const [isExpenseSystemAdministratorUser, setIsExpenseSystemAdministratorUser] = useState(false);
    const [isExpenseAdminUser, setIsExpenseAdminUser] = useState(false);
    const [isLTExpenseAssociateUser, setIsLTExpenseAssociateUser] = useState(false);
    const [isClinicianReimEntryUser, setIsClinicianReimEntryUser] = useState(false);
    const [isCreditMemoUser, setIsCreditMemoUser] = useState(false);
    const [isCreditCardUser, setIsCreditCardUser] = useState(false);
    const [isTimesheetSystemAdminUser, setIsTimesheetSystemAdminUser] = useState(false);
    const [isTimesheetAdminUser, setIsTimesheetAdminUser] = useState(false);
    const [isLTAssociateUser, setIsLTAssociateUser] = useState(false);
    const [isInvoicingUser, setIsInvoicingUser] = useState(false);
    const [isInvoicingSystemAdminUser, setIsInvoicingSystemAdminUser] = useState(false);
    const [isInvoicingAdminUser, setIsInvoicingAdminUser] = useState(false);
    const [isInvoicingLTAssociateUser, setIsInvoicingLTAssociateUser] = useState(false);
    const [userInformation, setUserInformation] = useState({});
    const [accessDenied, setAccessDenied] = useState(true);

    const contextValue = useMemo(
        () => ({
            adminUser,
            isAuthenticated,
            isTimesheetUser,
            isExpenseUser,
            isTimesheetSystemAdminUser,
            isTimesheetAdminUser,
            isLTAssociateUser,
            isExpenseSystemAdministratorUser,
            isExpenseAdminUser,
            isLTExpenseAssociateUser,
            isClinicianReimEntryUser,
            isCreditMemoUser,
            isCreditCardUser,
            isInvoicingUser,
            isInvoicingSystemAdminUser,
            isInvoicingAdminUser,
            isInvoicingLTAssociateUser,
            accessDenied,
            userInformation,
        }),
        [
            adminUser,
            isAuthenticated,
            isTimesheetUser,
            isExpenseUser,
            isTimesheetSystemAdminUser,
            isTimesheetAdminUser,
            isLTAssociateUser,
            isExpenseSystemAdministratorUser,
            isExpenseAdminUser,
            isLTExpenseAssociateUser,
            isClinicianReimEntryUser,
            isCreditMemoUser,
            isCreditCardUser,
            isInvoicingUser,
            isInvoicingSystemAdminUser,
            isInvoicingAdminUser,
            isInvoicingLTAssociateUser,
            accessDenied,
            userInformation,
        ]
    );

    const getUserInfo = async () => {
        if (authState?.isAuthenticated) {
            try {
                const info = await oktaAuth?.getUser();

                const { groups } = info;
                const isProd = process.env.REACT_APP_EXECUTION_ENVIRONMENT === 'PROD';

                if (groups) {
                    setAccessDenied(false);
                    const hasExpenseAccess = groups.some((g) =>
                        isProd ? ExpenseUserProdGroups.includes(g) : ExpenseUserGroups.includes(g)
                    );
                    const hasTimesheetAccess = groups.some((g) =>
                        isProd
                            ? TimesheetUserProdGroups.includes(g)
                            : TimesheetUserGroups.includes(g)
                    );
                    const isExpenseSystemAdministrator = groups.some((group) =>
                        isProd
                            ? group === ExpenseUserGroupProdEnum.ExpenseSystemAdministratorPROD
                            : group === ExpenseUserGroupEnum.ExpenseSystemAdministrator
                    );
                    const isExpenseAdmin = groups.some((group) =>
                        isProd
                            ? group === ExpenseUserGroupProdEnum.ExpenseAdministratorPROD
                            : group === ExpenseUserGroupEnum.ExpenseAdministrator
                    );
                    const isLTExpenseAssociate = groups.some((group) =>
                        isProd
                            ? group === ExpenseUserGroupProdEnum.LTExpenseAssociatePROD
                            : group === ExpenseUserGroupEnum.LTExpenseAssociate
                    );
                    const isClinicianReimEntry = groups.some((group) =>
                        isProd
                            ? group === ExpenseUserGroupProdEnum.ClinicianReimEntryPROD
                            : group === ExpenseUserGroupEnum.ClinicianReimEntry
                    );
                    const isCreditMemo = groups.some((group) =>
                        isProd
                            ? group === ExpenseUserGroupProdEnum.CreditMemoPROD
                            : group === ExpenseUserGroupEnum.CreditMemo
                    );
                    const isCreditCard = groups.some((group) =>
                        isProd
                            ? group === ExpenseUserGroupProdEnum.CreditCardPROD
                            : group === ExpenseUserGroupEnum.CreditCard
                    );
                    const isTimesheetSystemAdmin = groups.some((group) =>
                        isProd
                            ? group === TimesheetUserGroupProdEnum.SystemAdministratorPROD
                            : group === TimesheetUserGroupEnum.SystemAdministrator
                    );
                    const isTimesheetAdmin = groups.some((group) =>
                        isProd
                            ? group === TimesheetUserGroupProdEnum.TimesheetAdminPROD
                            : group === TimesheetUserGroupEnum.TimesheetAdmin
                    );
                    const isLTAssociate = groups.some((group) =>
                        isProd
                            ? group === TimesheetUserGroupProdEnum.LTAssociatePROD
                            : group === TimesheetUserGroupEnum.LTAssociate
                    );
                    const hasInvoicingAccess = groups.some((g) =>
                        isProd
                            ? InvoicingUserProdGroups.includes(g)
                            : InvoicingUserGroups.includes(g)
                    );
                    const isInvoicingSystemAdministrator = groups.some((group) =>
                        isProd
                            ? group === InvoicingUserGroupProdEnum.SystemAdministratorPROD
                            : group === InvoicingUserGroupEnum.SystemAdministrator
                    );
                    const isInvoicingAdministrator = groups.some((group) =>
                        isProd
                            ? group === InvoicingUserGroupProdEnum.AdministratorPROD
                            : group === InvoicingUserGroupEnum.Administrator
                    );
                    const isInvoicingLTAssociate = groups.some((group) =>
                        isProd
                            ? group === InvoicingUserGroupProdEnum.LTAssociatePROD
                            : group === InvoicingUserGroupEnum.LTAssociate
                    );
                    setUserInformation(info);

                    // Expenses permissions
                    setIsExpenseSystemAdministratorUser(isExpenseSystemAdministrator);
                    setIsExpenseAdminUser(isExpenseAdmin);
                    setIsLTExpenseAssociateUser(isLTExpenseAssociate);
                    setIsExpenseUser(hasExpenseAccess);
                    setIsClinicianReimEntryUser(isClinicianReimEntry);
                    setIsCreditMemoUser(isCreditMemo);
                    setIsCreditCardUser(isCreditCard);

                    // Timesheet permissions
                    setIsTimesheetSystemAdminUser(isTimesheetSystemAdmin);
                    setIsTimesheetAdminUser(isTimesheetAdmin);
                    setIsLTAssociateUser(isLTAssociate);
                    setIsTimesheetUser(hasTimesheetAccess);

                    // Invoicing permissions
                    setIsInvoicingUser(hasInvoicingAccess);
                    setIsInvoicingSystemAdminUser(isInvoicingSystemAdministrator);
                    setIsInvoicingAdminUser(isInvoicingAdministrator);
                    setIsInvoicingLTAssociateUser(isInvoicingLTAssociate);
                }
                setAdminUser(info);
                setIsAuthenticated(true);
            } catch (error) {
                console.error(error);
            }
        }
    };

    useEffect(() => {
        getUserInfo();
    }, [oktaAuth, authState]);

    return <AdminAuthContext.Provider value={contextValue}>{children}</AdminAuthContext.Provider>;
}
